/**
 * Paiements' writes, one per intent: the four corrections, settling a
 * refund, and deciding an approval request. Each is offered only with its
 * feature (the server checks again), names the `version` on screen, gives a
 * reason, and always carries the intent's Idempotency-Key: money writes
 * refuse to run without one (ADR-0016 §2.1).
 */
import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type {
  ApprovalRequest,
  FeePaymentDetail,
  PaymentActionResponse,
  PaymentAdjustment,
} from "@wallyo/contracts/console/payments";
import type { ConsoleClient } from "../../api/client";
import {
  approvePaymentRequest,
  issueCredit,
  issueRefund,
  markPaymentPaid,
  rejectPaymentRequest,
  settleRefund,
  waiveFee,
} from "../../api/console-api";
import { t } from "../../i18n";

export type PaymentActionKind = "refund" | "credit" | "waive" | "markPaid";
export type AdjustmentActionKind = "settle";
export type ApprovalActionKind = "approve" | "reject";
export type AnyPaymentActionKind = PaymentActionKind | AdjustmentActionKind | ApprovalActionKind;

const f = (name: string) => `${name}_STAFF_FEATURE_ENABLEMENT` as StaffFeatureId;

export const PAYMENT_FEATURES = {
  view: f("VIEW_PAYMENTS"),
  refund: f("ISSUE_REFUNDS"),
  credit: f("ISSUE_CREDITS"),
  waive: f("WAIVE_FEES"),
  markPaid: f("MARK_PAYMENTS_PAID"),
  /** Settling a refund is ISSUE_REFUNDS' (SETTLE_REFUND_STAFF_ACTION). */
  settle: f("ISSUE_REFUNDS"),
  approve: f("APPROVE_PAYMENT_REQUESTS"),
  reject: f("APPROVE_PAYMENT_REQUESTS"),
  export: f("EXPORT_PAYMENTS"),
} as const satisfies Record<string, StaffFeatureId>;

export const PAYMENT_ACTIONS: readonly PaymentActionKind[] = ["refund", "credit", "waive", "markPaid"];

/** The corrections this person may offer on this payment, and whether its state allows each now. */
export function paymentActions(
  payment: FeePaymentDetail,
  features: readonly StaffFeatureId[],
): { kind: PaymentActionKind; allowed: boolean }[] {
  const unpaid = payment.status === "pending" || payment.status === "failed";
  const allowed: Record<PaymentActionKind, boolean> = {
    refund: payment.status === "paid" && payment.refundableAmount > 0,
    credit: true,
    waive: unpaid,
    markPaid: unpaid,
  };
  return PAYMENT_ACTIONS.filter((kind) => features.includes(PAYMENT_FEATURES[kind])).map((kind) => ({
    kind,
    allowed: allowed[kind],
  }));
}

/** A refund Finance can settle: sent with no answer, or waiting for a manual transfer. */
export function canSettle(adjustment: PaymentAdjustment, features: readonly StaffFeatureId[]): boolean {
  return (
    features.includes(PAYMENT_FEATURES.settle) &&
    adjustment.kind === "refund" &&
    (adjustment.status === "pending" || adjustment.status === "awaiting_manual_transfer")
  );
}

/** Whether this person may decide the request, and why not. The server refuses the requester too. */
export function approvalBlock(
  request: ApprovalRequest,
  me: { staffMemberId: string; features: readonly StaffFeatureId[] },
): "feature" | "decided" | "own" | null {
  if (!me.features.includes(PAYMENT_FEATURES.approve)) return "feature";
  if (request.status !== "pending") return "decided";
  if (request.requestedBy.staffMemberId === me.staffMemberId) return "own";
  return null;
}

// ── Fields ────────────────────────────────────────────────────────────────

export type PaymentActionFields = {
  reason: string;
  /** As typed. Empty on a refund: the whole refundable amount. */
  amount: string;
  providerReference: string;
  outcome: "done" | "failed";
};

export const EMPTY_PAYMENT_FIELDS: PaymentActionFields = {
  reason: "",
  amount: "",
  providerReference: "",
  outcome: "done",
};

/** The server's bounds (server/http/validation/console-payments.ts). */
export const REASON_MIN = 3;
export const REASON_MAX = 500;
export const REFERENCE_MIN = 4;
export const REFERENCE_MAX = 100;
const REFERENCE = /^[A-Za-z0-9][A-Za-z0-9._:/-]*$/;

/** "25 000", "25000", "2,50" → a number; null when it isn't one. */
export function parseAmount(text: string): number | null {
  const cleaned = text.replace(/[\s  ]/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return value > 0 ? value : null;
}

export function referenceProblem(reference: string): string | null {
  const text = reference.trim();
  return text.length >= REFERENCE_MIN && text.length <= REFERENCE_MAX && REFERENCE.test(text)
    ? null
    : t("payments.problem.reference", { min: REFERENCE_MIN });
}

/** The first problem with what was typed, or null when it can be sent. */
export function paymentActionProblem(
  kind: AnyPaymentActionKind,
  fields: PaymentActionFields,
  payment: FeePaymentDetail | null,
): string | null {
  const reason = fields.reason.trim().length;
  if (reason < REASON_MIN || reason > REASON_MAX) return t("payments.problem.reason", { min: REASON_MIN });
  switch (kind) {
    case "refund": {
      if (!fields.amount.trim()) return null;
      const amount = parseAmount(fields.amount);
      if (amount === null) return t("payments.problem.amount");
      return payment && amount > payment.refundableAmount ? t("payments.problem.tooMuch") : null;
    }
    case "credit":
      return parseAmount(fields.amount) === null ? t("payments.problem.amount") : null;
    case "markPaid":
      return referenceProblem(fields.providerReference);
    case "settle":
      return fields.outcome === "done" ? referenceProblem(fields.providerReference) : null;
    default:
      return null;
  }
}

/** Sends a correction on the payment as shown. */
export function sendPaymentAction(
  client: ConsoleClient,
  payment: FeePaymentDetail,
  kind: PaymentActionKind,
  fields: PaymentActionFields,
  idempotencyKey: string,
): Promise<PaymentActionResponse> {
  const id = payment.platformFeePaymentId;
  const reason = fields.reason.trim();
  const version = payment.version;
  const options = { idempotencyKey };
  switch (kind) {
    case "refund": {
      const amount = fields.amount.trim() ? parseAmount(fields.amount) : null;
      return issueRefund(client, id, { ...(amount !== null ? { amount } : {}), reason, version }, options);
    }
    case "waive":
      return waiveFee(client, id, { reason, version }, options);
    case "markPaid":
      return markPaymentPaid(
        client,
        id,
        { providerReference: fields.providerReference.trim(), reason, version },
        options,
      );
    case "credit":
      return issueCredit(
        client,
        {
          workerId: payment.pro.workerId,
          amount: parseAmount(fields.amount) ?? 0,
          currency: payment.currency,
          reason,
          platformFeePaymentId: id,
          version,
        },
        options,
      );
  }
}

/** Records how a refund ended, at the correction's version. */
export function sendSettle(
  client: ConsoleClient,
  adjustment: PaymentAdjustment,
  fields: PaymentActionFields,
  idempotencyKey: string,
) {
  return settleRefund(
    client,
    adjustment.paymentAdjustmentId,
    {
      outcome: fields.outcome,
      // Only a refund done has a reference at the provider.
      providerReference: fields.outcome === "done" ? fields.providerReference.trim() : null,
      reason: fields.reason.trim(),
      version: adjustment.version,
    },
    { idempotencyKey },
  );
}

export function sendApprovalDecision(
  client: ConsoleClient,
  request: ApprovalRequest,
  kind: ApprovalActionKind,
  reason: string,
  idempotencyKey: string,
) {
  const send = kind === "approve" ? approvePaymentRequest : rejectPaymentRequest;
  return send(
    client,
    request.approvalRequestId,
    { reason: reason.trim(), version: request.version },
    { idempotencyKey },
  );
}
